import { useEffect, useState, useRef } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useIsBrandAdmin } from '../lib/brandAdmin'
import { searchFilter } from '../lib/search'
import { supabase } from '../lib/supabase'
import { Plus, Search, X, AlertTriangle, Edit2, Package, Trash2, Copy, Upload, Download } from 'lucide-react'
import CatalogImport from './CatalogImport'
import { exportToCsv } from '../lib/exportCsv'
import ProductPicker from '../components/ProductPicker'
import HelpLink from '../components/HelpLink'

// Product types that carry a Bill of Materials.
// Bundles: BOM = component SKUs. Finished goods: BOM = purchased components (tubes, fill, cartons) used for cost / cash planning.
// Bundles only. Finished goods have no purchasing BOM: component costs come from the component POs themselves (Oct 2026)
const hasBom = t => t === 'virtual_bundle' || t === 'physical_bundle'

const EMPTY = {
  sku: '', name: '', description: '', category: '', unit_of_measure: 'each',
  size: '', color: '', asin: '', upc: '', cost_price: '', retail_price: '',
  weight_oz: '', length_in: '', width_in: '', height_in: '',
  reorder_point: '', reorder_quantity: '', supplier_id: '',
  product_type: 'finished_good', replenishment_status: 'replenish', is_active: true,
  sourcing_type: 'overseas', production_lead_time_days: '', target_days_of_supply: '90', fba_target_days: '60', min_shelf_life_days: '',
  target_wos_fba: '12', target_wos_warehouse: '12', default_destination: 'warehouse',
  shopify_enabled: true, shopify_replen_status: 'replenish', amazon_enabled: false, amazon_replen_status: 'replenish', channels: 'both', replenishment: 'replenish',
  fba_prep_new_upc: false, fba_prep_ingredient_label: false, fba_prep_poly_bag: false, fba_prep_bubble_wrap: false,
  fba_prep_fragile_glass: false, fba_prep_suffocation_warning: false, fba_prep_expiration_label: false, fba_prep_hazmat: false,
  fba_prep_notes: ''
}

const FBA_PREP_FLAGS = [
  { key: 'fba_prep_new_upc', label: 'New UPC / FNSKU label' },
  { key: 'fba_prep_ingredient_label', label: 'Ingredient label' },
  { key: 'fba_prep_poly_bag', label: 'Poly bag' },
  { key: 'fba_prep_bubble_wrap', label: 'Bubble wrap' },
  { key: 'fba_prep_fragile_glass', label: 'Fragile / glass' },
  { key: 'fba_prep_suffocation_warning', label: 'Suffocation warning' },
  { key: 'fba_prep_expiration_label', label: 'Expiration label' },
  { key: 'fba_prep_hazmat', label: 'Hazmat' }
]

const REPLENISHMENT_BADGE = { replenish: 'badge-success', sunset: 'badge-warning', new: 'badge-info' }
const REPLENISHMENT_LABEL = { replenish: 'Replenish', sunset: 'Sunset', new: 'New' }
const TYPE_BADGE = { finished_good: 'badge-accent', component: 'badge-neutral', raw_material: 'badge-neutral' }
const TYPE_LABEL = { finished_good: 'Finished Good', component: 'Component', raw_material: 'Raw Material' }

export default function Catalog() {
  const { activeBrand } = useAuth()
  const isBrandAdmin = useIsBrandAdmin()
  const [products, setProducts] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [categories, setCategories] = useState([])
  const [search, setSearch] = useState('')
  const [filterType, setFilterType] = useState('')
  const [filterChannel, setFilterChannel] = useState('')
  const [modal, setModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [formError, setFormError] = useState('')
  const [bomLines, setBomLines] = useState([]) // for virtual bundle BOM editor
  const [bomLoading, setBomLoading] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const bomLoadingFor = useRef(null) // tracks which product ID we're currently loading BOM for

  useEffect(() => { if (activeBrand) load() }, [activeBrand])

  async function load() {
    setLoading(true)
    const [{ data: prods }, { data: sups }, { data: cats }] = await Promise.all([
      supabase.from('products').select('*, suppliers(name)').eq('brand_id', activeBrand.id).is('deleted_at', null).order('sku'),
      supabase.from('suppliers').select('id, name').eq('brand_id', activeBrand.id).order('name'),
      supabase.from('brand_categories').select('id, name').eq('brand_id', activeBrand.id).order('name')
    ])
    setProducts(prods || [])
    setSuppliers(sups || [])
    setCategories(cats || [])
    setLoading(false)
  }

  function openNew() { setEditing(null); setForm(EMPTY); setFormError(''); setBomLines([]); setModal(true) }
  function openEdit(p) {
    setEditing(p)
    setFormError('')
    setBomLines([])
    setForm({
      ...EMPTY, ...p,
      supplier_id: p.supplier_id || '',
      cost_price: p.cost_price || '',
      retail_price: p.retail_price || '',
      reorder_point: p.reorder_point || '',
      reorder_quantity: p.reorder_quantity || '',
      size: p.size || '',
      color: p.color || '',
      asin: p.asin || '',
      upc: p.upc || '',
      weight_oz: p.weight_oz || '',
      length_in: p.length_in || '',
      width_in: p.width_in || '',
      height_in: p.height_in || '',
      product_type: p.product_type || 'finished_good',
      replenishment_status: p.replenishment_status || 'replenish',
      sourcing_type: p.sourcing_type || 'overseas',
      production_lead_time_days: p.production_lead_time_days || '',
      target_days_of_supply: p.target_days_of_supply || '90',
      fba_target_days: p.fba_target_days || '60',
      target_wos_fba: p.target_wos_fba ?? '12',
      target_wos_warehouse: p.target_wos_warehouse ?? '12',
      default_destination: p.default_destination || 'warehouse',
      min_shelf_life_days: p.min_shelf_life_days || '',
      shopify_enabled: p.shopify_enabled !== false,
      shopify_replen_status: p.shopify_replen_status || 'replenish',
      amazon_enabled: p.amazon_enabled || false,
      amazon_replen_status: p.amazon_replen_status || 'replenish',
      // Sunset when every channel it sells on is Sunset (same rule the Planner uses)
      replenishment: ((p.shopify_enabled !== false && p.shopify_replen_status !== 'sunset') || (p.amazon_enabled && p.amazon_replen_status !== 'sunset')) ? 'replenish' : 'sunset',
      fba_prep_new_upc: p.fba_prep_new_upc || false,
      fba_prep_ingredient_label: p.fba_prep_ingredient_label || false,
      fba_prep_poly_bag: p.fba_prep_poly_bag || false,
      fba_prep_bubble_wrap: p.fba_prep_bubble_wrap || false,
      fba_prep_fragile_glass: p.fba_prep_fragile_glass || false,
      fba_prep_suffocation_warning: p.fba_prep_suffocation_warning || false,
      fba_prep_expiration_label: p.fba_prep_expiration_label || false,
      fba_prep_hazmat: p.fba_prep_hazmat || false,
      fba_prep_notes: p.fba_prep_notes || ''
    })
    if (hasBom(p.product_type)) {
      loadBomLines(p.id)
    }
    setModal(true)
  }
  function closeModal() { bomLoadingFor.current = null; setModal(false); setEditing(null); setFormError(''); setBomLines([]) }

  async function loadBomLines(productId) {
    bomLoadingFor.current = productId
    setBomLoading(true)
    setBomLines([])
    const { data } = await supabase
      .from('bill_of_materials')
      .select('component_product_id, quantity')
      .eq('parent_product_id', productId)
      .eq('brand_id', activeBrand.id)
    // Discard result if the user opened a different product before this fetch finished
    if (bomLoadingFor.current !== productId) return
    setBomLines((data || []).map(r => ({ ...r, quantity: String(r.quantity) })))
    setBomLoading(false)
  }
  const sf = k => v => setForm(f => ({ ...f, [k]: v }))

  async function checkDuplicate(field, value, excludeId) {
    if (!value) return false
    let query = supabase.from('products').select('id').eq('brand_id', activeBrand.id).eq(field, value).is('deleted_at', null)
    if (excludeId) query = query.neq('id', excludeId)
    const { data } = await query
    return data && data.length > 0
  }

  async function save() {
    setFormError('')
    setSaving(true)

    // Components don't need a SKU: a blank one gets a placeholder from the name
    const sku = form.sku?.trim() || (form.product_type === 'component'
      ? 'COMP-' + form.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40)
      : '')

    // Check duplicates
    const skuDup = await checkDuplicate('sku', sku, editing?.id)
    if (skuDup) { setFormError('A product with this SKU already exists.'); setSaving(false); return }

    if (form.upc) {
      const upcDup = await checkDuplicate('upc', form.upc, editing?.id)
      if (upcDup) { setFormError('A product with this UPC already exists.'); setSaving(false); return }
    }

    const fbaPrep = form.amazon_enabled || !!form.asin
    const payload = {
      brand_id: activeBrand.id,
      sku,
      name: form.name,
      description: form.description || null,
      category: form.category || null,
      unit_of_measure: form.unit_of_measure || 'each',
      size: form.size || null,
      color: form.color || null,
      asin: form.asin || null,
      upc: form.upc || null,
      cost_price: form.cost_price ? parseFloat(form.cost_price) : null,
      retail_price: form.retail_price ? parseFloat(form.retail_price) : null,
      weight_oz: form.weight_oz ? parseFloat(form.weight_oz) : null,
      length_in: form.length_in ? parseFloat(form.length_in) : null,
      width_in: form.width_in ? parseFloat(form.width_in) : null,
      height_in: form.height_in ? parseFloat(form.height_in) : null,
      reorder_point: form.reorder_point ? parseInt(form.reorder_point) : null,
      reorder_quantity: form.reorder_quantity ? parseInt(form.reorder_quantity) : null,
      supplier_id: form.supplier_id || null,
      product_type: form.product_type || 'finished_good',
      replenishment_status: form.replenishment_status || 'replenish',
      is_active: form.is_active,
      sourcing_type: form.sourcing_type || 'overseas',
      production_lead_time_days: form.production_lead_time_days ? parseInt(form.production_lead_time_days) : null,
      target_days_of_supply: form.target_days_of_supply ? parseInt(form.target_days_of_supply) : 90,
      fba_target_days: form.fba_target_days ? parseInt(form.fba_target_days) : 60,
      target_wos_fba: form.target_wos_fba === '' ? 12 : Number(form.target_wos_fba),
      target_wos_warehouse: form.target_wos_warehouse === '' ? 12 : Number(form.target_wos_warehouse),
      default_destination: form.default_destination || 'warehouse',
      min_shelf_life_days: form.min_shelf_life_days ? parseInt(form.min_shelf_life_days) : null,
      // Sales channels are no longer edited here: one Replenishment setting drives both channel statuses.
      // Amazon is on whenever the product has an ASIN (never switched off automatically).
      shopify_enabled: form.shopify_enabled,
      shopify_replen_status: form.replenishment === 'sunset' ? 'sunset' : (form.shopify_replen_status === 'sunset' ? 'replenish' : form.shopify_replen_status || 'replenish'),
      amazon_enabled: form.amazon_enabled || !!form.asin,
      amazon_replen_status: form.replenishment === 'sunset' ? 'sunset' : (form.amazon_replen_status === 'sunset' ? 'replenish' : form.amazon_replen_status || 'replenish'),
      fba_prep_new_upc: fbaPrep ? !!form.fba_prep_new_upc : false,
      fba_prep_ingredient_label: fbaPrep ? !!form.fba_prep_ingredient_label : false,
      fba_prep_poly_bag: fbaPrep ? !!form.fba_prep_poly_bag : false,
      fba_prep_bubble_wrap: fbaPrep ? !!form.fba_prep_bubble_wrap : false,
      fba_prep_fragile_glass: fbaPrep ? !!form.fba_prep_fragile_glass : false,
      fba_prep_suffocation_warning: fbaPrep ? !!form.fba_prep_suffocation_warning : false,
      fba_prep_expiration_label: fbaPrep ? !!form.fba_prep_expiration_label : false,
      fba_prep_hazmat: fbaPrep ? !!form.fba_prep_hazmat : false,
      fba_prep_notes: fbaPrep ? (form.fba_prep_notes || null) : null
    }

    let error
    let savedId = editing?.id
    if (editing) {
      const result = await supabase.from('products').update(payload).eq('id', editing.id)
      error = result.error
    } else {
      const result = await supabase.from('products').insert(payload).select('id').single()
      error = result.error
      if (!error) savedId = result.data?.id
    }
    if (error) { setFormError('Save failed: ' + error.message); setSaving(false); return }

    // Save BOM lines for bundles
    if (hasBom(form.product_type) && savedId && !bomLoading) {
      await supabase.from('bill_of_materials').delete().eq('parent_product_id', savedId).eq('brand_id', activeBrand.id)
      const validLines = bomLines.filter(l => l.component_product_id)
      if (validLines.length > 0) {
        await supabase.from('bill_of_materials').insert(
          validLines.map(l => ({
            brand_id: activeBrand.id,
            parent_product_id: savedId,
            component_product_id: l.component_product_id,
            quantity: parseFloat(l.quantity) || 1
          }))
        )
      }
    }

    setSaving(false); closeModal(); load()
  }

  async function deleteProduct(p) {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return
    await supabase.from('products').update({ deleted_at: new Date().toISOString() }).eq('id', p.id)
    load()
  }

  // A complete SKU or ASIN shows only that product; partial text matches name, SKU, category or ASIN (lib/search)
  let filtered = products.filter(searchFilter(products, search, (p, q) =>
    (p.name || '').toLowerCase().includes(q) ||
    (p.sku || '').toLowerCase().includes(q) ||
    (p.category || '').toLowerCase().includes(q) ||
    (p.asin || '').toLowerCase().includes(q)
  ))
  if (filterType) filtered = filtered.filter(p => p.product_type === filterType)
  if (filterChannel === 'amazon') filtered = filtered.filter(p => p.amazon_enabled)
  else if (filterChannel === 'shopify') filtered = filtered.filter(p => p.shopify_enabled)
  else if (filterChannel === 'both') filtered = filtered.filter(p => p.amazon_enabled && p.shopify_enabled)
  else if (filterChannel === 'amazon_only') filtered = filtered.filter(p => p.amazon_enabled && !p.shopify_enabled)
  else if (filterChannel === 'shopify_only') filtered = filtered.filter(p => p.shopify_enabled && !p.amazon_enabled)

  function exportCatalog() {
    const header = ['SKU', 'Name', 'Type', 'Category', 'Size', 'Color', 'Supplier', 'Cost Price', 'Retail Price', 'Reorder Point', 'Reorder Qty', 'Status', 'Replenishment']
    const rows = filtered.map(p => [
      p.sku, p.name, p.product_type, p.category, p.size, p.color,
      p.suppliers?.name, p.cost_price, p.retail_price,
      p.reorder_point, p.reorder_quantity,
      p.is_active ? 'Active' : 'Inactive', p.replenishment_status
    ])
    exportToCsv('catalog.csv', [header, ...rows])
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Catalog<HelpLink section="catalog" /></h1>
        <div className="page-header-actions">
          <div className="search-input-wrap"><Search size={14} /><input className="input" placeholder="Search SKU, name or ASIN..." value={search} onChange={e => setSearch(e.target.value)} /></div>
          <select className="select" style={{ width: 'auto' }} value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">All Types</option>
            <option value="finished_good">Finished Goods</option>
            <option value="component">Components</option>
            <option value="raw_material">Raw Materials</option>
          </select>
          <select className="select" style={{ width: 'auto' }} value={filterChannel} onChange={e => setFilterChannel(e.target.value)}>
            <option value="">All Channels</option>
            <option value="amazon">Amazon enabled</option>
            <option value="shopify">Shopify enabled</option>
            <option value="both">Amazon + Shopify</option>
            <option value="amazon_only">Amazon only</option>
            <option value="shopify_only">Shopify only</option>
          </select>
          <button className="btn btn-secondary" onClick={exportCatalog}><Download size={14} />Export CSV</button>
          <button className="btn btn-secondary" onClick={() => setImportOpen(true)}><Upload size={14} />Import CSV</button>
          <button className="btn btn-primary" onClick={openNew}><Plus size={14} />Add Product</button>
        </div>
      </div>

      <div className="card">
        {loading ? <div style={{ padding: '40px', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          : filtered.length === 0 ? <div className="empty-state"><Package size={40} /><p>No products found</p></div>
          : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>SKU</th><th>Name</th><th>Type</th><th>Category</th><th>Size</th><th>Color</th><th>Supplier</th><th>Retail</th>{!isBrandAdmin && <th>Reorder Pt</th>}<th>Status</th><th></th></tr>
                </thead>
                <tbody>
                  {filtered.map(p => (
                    <tr key={p.id}>
                      <td>
                        <span className="mono">{p.sku}</span>
                        {p.product_type === 'virtual_bundle' && <span className="badge badge-accent" style={{ fontSize: '10px', marginLeft: '6px' }}>Virtual</span>}
                        {p.product_type === 'physical_bundle' && <span className="badge badge-info" style={{ fontSize: '10px', marginLeft: '6px' }}>Bundle</span>}
                      </td>
                      <td style={{ fontWeight: 500 }}>{p.name}</td>
                      <td><span className={`badge ${TYPE_BADGE[p.product_type] || 'badge-neutral'}`} style={{ fontSize: '10px' }}>{TYPE_LABEL[p.product_type] || p.product_type}</span></td>
                      <td>{p.category || '—'}</td>
                      <td>{p.size || '—'}</td>
                      <td>{p.color || '—'}</td>
                      <td>{p.suppliers?.name || '—'}</td>
                      <td>{p.retail_price ? `$${Number(p.retail_price).toFixed(2)}` : '—'}</td>
                      {!isBrandAdmin && <td>{p.reorder_point ? <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{p.reorder_point} <AlertTriangle size={12} style={{ color: 'var(--warning)' }} /></span> : '—'}</td>}
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                          <span className={`badge ${p.is_active ? 'badge-success' : 'badge-neutral'}`} style={{ fontSize: '10px' }}>{p.is_active ? 'Active' : 'Inactive'}</span>
                          <span className={`badge ${REPLENISHMENT_BADGE[p.replenishment_status] || 'badge-neutral'}`} style={{ fontSize: '10px' }}>{REPLENISHMENT_LABEL[p.replenishment_status] || p.replenishment_status}</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '2px' }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => openEdit(p)}><Edit2 size={13} /></button>
                          {!isBrandAdmin && <button className="btn btn-ghost btn-sm" onClick={() => deleteProduct(p)}><Trash2 size={13} style={{ color: 'var(--danger)' }} /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <h2>{editing ? 'Edit Product' : 'Add Product'}</h2>
              <button className="btn btn-ghost btn-sm" onClick={closeModal}><X size={16} /></button>
            </div>
            <div className="modal-body">
              {formError && <div className="alert alert-danger">{formError}</div>}

              <div className="form-grid">
                <div className="form-group"><label className="form-label">SKU{form.product_type === 'component' ? ' (optional)' : ' *'}</label><input className="input" value={form.sku} onChange={e => sf('sku')(e.target.value)} placeholder="SKU-001" /></div>
                <div className="form-group"><label className="form-label">Name *</label><input className="input" value={form.name} onChange={e => sf('name')(e.target.value)} placeholder="Product name" /></div>
              </div>

              <div className="form-grid-3">
                <div className="form-group">
                  <label className="form-label">Product Type</label>
                  <select className="select" value={form.product_type} onChange={e => sf('product_type')(e.target.value)}>
                    <option value="finished_good">Finished Good</option>
                    <option value="virtual_bundle">Virtual Bundle</option>
                    <option value="physical_bundle">Physical Bundle</option>
                    <option value="component">Component</option>
                    <option value="raw_material">Raw Material</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select className="select" value={form.category} onChange={e => sf('category')(e.target.value)}>
                    <option value="">— None —</option>
                    {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Unit of Measure</label>
                  <select className="select" value={form.unit_of_measure} onChange={e => sf('unit_of_measure')(e.target.value)}>
                    <option>each</option><option>case</option><option>dozen</option><option>pair</option><option>set</option><option>lb</option><option>oz</option><option>ml</option><option>g</option>
                  </select>
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group"><label className="form-label">Size / Variant</label><input className="input" value={form.size} onChange={e => sf('size')(e.target.value)} placeholder="e.g. 2oz, Large, 100ml" /></div>
                <div className="form-group"><label className="form-label">Color</label><input className="input" value={form.color} onChange={e => sf('color')(e.target.value)} placeholder="e.g. Blue, Red, Forest Green" /></div>
                <div className="form-group"><label className="form-label">UPC</label><input className="input" value={form.upc} onChange={e => sf('upc')(e.target.value)} placeholder="012345678901" /></div>
              </div>

              <div className="form-grid">
                <div className="form-group"><label className="form-label">Amazon ASIN</label><input className="input" value={form.asin} onChange={e => sf('asin')(e.target.value)} placeholder="B0XXXXXXXX" /></div>
                <div className="form-group"><label className="form-label">Primary Supplier</label>
                  <select className="select" value={form.supplier_id} onChange={e => sf('supplier_id')(e.target.value)}>
                    <option value="">— None —</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-group"><label className="form-label">Description</label><textarea className="textarea" rows={2} value={form.description} onChange={e => sf('description')(e.target.value)} /></div>

              {/* Weight & Dimensions */}
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Weight & Dimensions</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
                <div className="form-group"><label className="form-label">Weight (oz)</label><input className="input" type="number" step="0.001" value={form.weight_oz} onChange={e => sf('weight_oz')(e.target.value)} placeholder="0.000" /></div>
                <div className="form-group"><label className="form-label">Length (in)</label><input className="input" type="number" step="0.01" value={form.length_in} onChange={e => sf('length_in')(e.target.value)} placeholder="0.00" /></div>
                <div className="form-group"><label className="form-label">Width (in)</label><input className="input" type="number" step="0.01" value={form.width_in} onChange={e => sf('width_in')(e.target.value)} placeholder="0.00" /></div>
                <div className="form-group"><label className="form-label">Height (in)</label><input className="input" type="number" step="0.01" value={form.height_in} onChange={e => sf('height_in')(e.target.value)} placeholder="0.00" /></div>
              </div>

              <div className="form-grid">
                <div className="form-group"><label className="form-label">Retail Price</label><input className="input" type="number" step="0.01" value={form.retail_price} onChange={e => sf('retail_price')(e.target.value)} placeholder="0.00" /></div>
                {!isBrandAdmin && <div className="form-group"><label className="form-label">Reorder Point</label><input className="input" type="number" value={form.reorder_point} onChange={e => sf('reorder_point')(e.target.value)} placeholder="Qty to trigger alert" /></div>}
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Replenishment</label>
                  <select className="select" value={form.replenishment} onChange={e => sf('replenishment')(e.target.value)}>
                    <option value="replenish">Replenish</option>
                    <option value="sunset">Sunset — sell through, no reorders</option>
                  </select>
                </div>
              </div>

              {(form.amazon_enabled || !!form.asin) && (
                <>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '4px' }}>FBA Prep Requirements</div>
                  <div style={{ padding: '12px 14px', background: 'var(--bg-3)', borderRadius: 'var(--radius)', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                      {FBA_PREP_FLAGS.map(f => (
                        <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                          <input type="checkbox" checked={!!form[f.key]} onChange={e => sf(f.key)(e.target.checked)} />
                          {f.label}
                        </label>
                      ))}
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Prep notes (free-form instructions for ShipMonk)</label>
                      <textarea className="textarea" rows={2} value={form.fba_prep_notes} onChange={e => sf('fba_prep_notes')(e.target.value)} placeholder="e.g. Apply FNSKU over existing UPC; box of 6 = 1 FBA unit" />
                    </div>
                  </div>
                </>
              )}

              {form.product_type !== 'virtual_bundle' && <>{!isBrandAdmin && <><div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Reorder Settings</div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Sourcing Type</label>
                  <select className="select" value={form.sourcing_type} onChange={e => sf('sourcing_type')(e.target.value)}>
                    <option value="overseas">Overseas (45 day transit)</option>
                    <option value="domestic">Domestic (7 day transit)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Production Lead Time (days)</label>
                  <input className="input" type="number" value={form.production_lead_time_days} onChange={e => sf('production_lead_time_days')(e.target.value)} placeholder="e.g. 45" />
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Target Days of Supply</label>
                  <input className="input" type="number" value={form.target_days_of_supply} onChange={e => sf('target_days_of_supply')(e.target.value)} placeholder="90" />
                </div>
                {(form.channels === 'both' || form.channels === 'amazon') && (
                  <div className="form-group">
                    <label className="form-label">FBA Target Days</label>
                    <input className="input" type="number" value={form.fba_target_days} onChange={e => sf('fba_target_days')(e.target.value)} placeholder="60" />
                  </div>
                )}
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Min Shelf Life Required (days)</label>
                  <input className="input" type="number" value={form.min_shelf_life_days} onChange={e => sf('min_shelf_life_days')(e.target.value)} placeholder="e.g. 180" />
                  <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '4px' }}>Only inventory meeting this threshold counts toward days-on-hand and reorder calcs</div>
                </div>
              </div></>}
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Planning (Weeks of Supply)</div>
              <div className="form-grid-3">
                <div className="form-group">
                  <label className="form-label">Warehouse Target (weeks)</label>
                  <input className="input" type="number" value={form.target_wos_warehouse} onChange={e => sf('target_wos_warehouse')(e.target.value)} placeholder="12" />
                </div>
                <div className="form-group">
                  <label className="form-label">FBA Target (weeks)</label>
                  <input className="input" type="number" value={form.target_wos_fba} onChange={e => sf('target_wos_fba')(e.target.value)} placeholder="12" />
                </div>
                <div className="form-group">
                  <label className="form-label">Default PO Destination</label>
                  <select className="select" value={form.default_destination} onChange={e => sf('default_destination')(e.target.value)}>
                    <option value="warehouse">Warehouse</option>
                    <option value="fba">FBA</option>
                    <option value="split">Split — FBA direct + Warehouse</option>
                  </select>
                </div>
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '-4px' }}>12 weeks ≈ 3 months. Default destination is where planned orders land until you split the PO into shipments. Split = the supplier can ship straight to FBA; the Planner recommends how much goes to FBA vs the warehouse.</div>
            </> }
            {/* BOM Editor for virtual/physical bundles */}
            {hasBom(form.product_type) && (
              <div style={{ padding: '0 24px 16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px' }}>
                  {form.product_type === 'finished_good' ? 'Bill of Materials — Purchased Components (for cost & cash planning)' : 'Bill of Materials — Components'}
                </div>
                {bomLoading ? <div className="spinner" /> : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {bomLines.map((line, i) => {
                      const otherSelectedIds = bomLines
                        .filter((_, j) => j !== i)
                        .map(l => l.component_product_id)
                        .filter(Boolean)
                      const eligible = products.filter(p => p.id !== form.id && p.product_type !== 'virtual_bundle')
                      return (
                      <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <ProductPicker
                          products={eligible}
                          value={line.component_product_id || ''}
                          onChange={id => {
                            const updated = [...bomLines]
                            updated[i] = { ...updated[i], component_product_id: id }
                            setBomLines(updated)
                          }}
                          disabledIds={otherSelectedIds}
                          disabledLabel="added"
                          placeholder="Search for a component..."
                          style={{ flex: 1 }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>Qty</span>
                          <input
                            className="input"
                            type="number"
                            style={{ width: '70px' }}
                            value={line.quantity}
                            min="1"
                            onChange={e => {
                              const updated = [...bomLines]
                              updated[i] = { ...updated[i], quantity: e.target.value }
                              setBomLines(updated)
                            }}
                          />
                        </div>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => setBomLines(bomLines.filter((_, j) => j !== i))}
                        >
                          <Trash2 size={13} style={{ color: 'var(--danger)' }} />
                        </button>
                      </div>
                      )
                    })}
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{ width: 'fit-content' }}
                      onClick={() => setBomLines([...bomLines, { component_product_id: '', quantity: 1 }])}
                    >
                      <Plus size={13} /> Add Component
                    </button>
                  </div>
                )}
              </div>
            )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving || (!form.sku && form.product_type !== 'component') || !form.name}>
                {saving ? 'Saving...' : editing ? 'Update Product' : 'Add Product'}
              </button>
            </div>
          </div>
        </div>
      )}

      <CatalogImport
        open={importOpen}
        onClose={() => setImportOpen(false)}
        activeBrand={activeBrand}
        suppliers={suppliers}
        categories={categories}
        onDone={load}
      />
    </div>
  )
}
